import { Combobox } from "@headlessui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ChevronDown from "../../components/Atomos/Icons/ChevronDown";
import Close from "../../components/Atomos/Icons/Close";
import Pencil from "../../components/Atomos/Icons/ViewMode/Pencil";
import Trash from "../../components/Atomos/Icons/EditMode/Trash";
import { EmailSenderDropdownOption } from "../../interfaces/models/IEmailSenderUsername";
import { useStaffMode } from "../../hooks/StaffModeProvider";
import { includesFoldedSearch } from "../../utils/foldSearchText";

export const CREATE_NEW_FROM_EMAIL_VALUE = "__create_new_from_email__";

type FromEmailSenderDropdownProps = {
  title: string;
  name?: string;
  options: EmailSenderDropdownOption[];
  selectedEmail: string;
  onSelect: (option: EmailSenderDropdownOption) => void;
  onEdit: (option: EmailSenderDropdownOption) => void;
  onDelete: (option: EmailSenderDropdownOption) => void;
  onCreateNew: () => void;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  errormsj?: string;
  required?: boolean;
  placeholder?: string;
};

export default function FromEmailSenderDropdown ({
  title,
  name,
  options,
  selectedEmail,
  onSelect,
  onEdit,
  onDelete,
  onCreateNew,
  loading = false,
  disabled = false,
  error = false,
  errormsj,
  required = false,
  placeholder,
}: FromEmailSenderDropdownProps): React.ReactElement {
  const { isStaffMode } = useStaffMode();
  const color = isStaffMode ? "secondary" : "primary";
  const [dataQuery, setDataQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel =
    options.find((option) => option.id === selectedEmail)?.name ?? selectedEmail ?? "";

  const filteredOptions = useMemo(() => {
    if (!dataQuery.trim()) {
      return options;
    }
    return options.filter((option) =>
      includesFoldedSearch(option.name, dataQuery),
    );
  }, [dataQuery, options]);

  useEffect(() => {
    if (!selectedEmail && inputRef.current) {
      inputRef.current.value = "";
    }
  }, [selectedEmail]);

  const handleClearSelection = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) {
      return;
    }
    setDataQuery("");
    onSelect({ id: "", name: "", sender: "", isCustom: false });
  };

  const stopActionEvent = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const runDropdownAction = (action: () => void) => {
    inputRef.current?.blur();
    action();
  };

  return (
    <div className={`flex flex-col gap-2 md:gap-[.625rem] h-max w-full ${error ? "bottom-0 relative" : ""}`}>
      <label className="text-base md:text-lg font-medium text-gray-font">
        {title} {required ? <span className="text-primary"> *</span> : null}
      </label>

      <Combobox
        value={selectedEmail || null}
        onChange={(value: string | null) => {
          setDataQuery("");
          if (!value) {
            return;
          }
          if (value === CREATE_NEW_FROM_EMAIL_VALUE) {
            runDropdownAction(onCreateNew);
            return;
          }
          const option = options.find((item) => item.id === value);
          if (option) {
            onSelect(option);
          }
        }}
        disabled={disabled || loading}
      >
        {({ open }) => (
          <div className="relative">
            <div className={`relative ${error ? "errorInput" : ""}`}>
              <Combobox.Button className="w-full">
                <div className="relative flex flex-row">
                  <Combobox.Input
                    ref={inputRef}
                    displayValue={() => selectedLabel}
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    className={`${error ? "text-primary" : "text-input-gray"} cursor-pointer w-full`}
                    style={
                      selectedLabel
                        ? {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "left",
                          direction: "ltr",
                        }
                        : undefined
                    }
                    onChange={(event) => setDataQuery(event.currentTarget.value)}
                    onFocus={() => setDataQuery("")}
                    name={name ?? title}
                    readOnly={false}
                    placeholder={loading ? "Loading senders…" : placeholder ?? "Select from email"}
                  />
                  {selectedLabel ? (
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 right-10 h-4 w-4 md:h-5 md:w-5 cursor-pointer z-1 ${disabled ? "fill-gray-300 pointer-events-none" : "fill-gray-font"
                        }`}
                      onClick={handleClearSelection}
                    >
                      <Close />
                    </div>
                  ) : null}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <div
                      className={`${disabled ? "text-gray-200" : "text-gray-font"
                        } w-5 h-5 md:w-6 md:h-6 ${open ? "rotate-180 duration-75" : ""
                        } ${disabled ? "fill-gray-300" : "fill-gray-font"}`}
                    >
                      <ChevronDown />
                    </div>
                  </div>
                </div>
              </Combobox.Button>
            </div>

            <Combobox.Options className={`absolute top-14 w-full max-h-48 z-30 ${!open ? "hidden" : ""} bg-white rounded-sm shadow-2xl`}>
              <div className="max-h-48 overflow-y-auto">
                {filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.id}
                    value={option.id}
                    className={({ active }) =>
                      `px-2 md:px-4 py-2 text-base md:text-lg leading-[19px] text-left cursor-pointer ${active || selectedEmail === option.id
                        ? `bg-${color} !text-white`
                        : "bg-white hover:bg-gray-50"
                      }`
                    }
                  >
                    {({ active }) => {
                      const isHighlighted = active || selectedEmail === option.id;
                      return (
                        <div className="flex items-center justify-between gap-3">
                          <span className="truncate min-w-0 flex-1">{option.name}</span>
                          {option.isCustom ? (
                            <div className="flex shrink-0 items-center gap-2">
                              <button
                                type="button"
                                aria-label={`Edit ${option.name}`}
                                className={`flex h-7 w-7 items-center justify-center rounded transition-transform hover:scale-110 ${isHighlighted ? "text-white" : "text-secondary"
                                  }`}
                                onMouseDown={stopActionEvent}
                                onClick={(event) => {
                                  stopActionEvent(event);
                                  runDropdownAction(() => onEdit(option));
                                }}
                              >
                                <Pencil width={16} height={16} color="currentColor" />
                              </button>
                              <button
                                type="button"
                                aria-label={`Delete ${option.name}`}
                                className="flex h-7 w-7 items-center justify-center rounded text-primary transition-transform hover:scale-110"
                                onMouseDown={stopActionEvent}
                                onClick={(event) => {
                                  stopActionEvent(event);
                                  runDropdownAction(() => onDelete(option));
                                }}
                              >
                                <Trash className="h-4 w-4" />
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    }}
                  </Combobox.Option>
                ))}

                {filteredOptions.length === 0 ? (
                  <div className="px-2 md:px-4 py-2 text-base md:text-lg text-gray-500 text-center">
                    No results found
                  </div>
                ) : null}
              </div>

              <Combobox.Option
                value={CREATE_NEW_FROM_EMAIL_VALUE}
                className="border-t border-gray-200 px-2 md:px-4 py-2 text-base md:text-lg font-medium text-primary text-left cursor-pointer bg-white hover:bg-gray-50"
              >
                Create new from email
              </Combobox.Option>
            </Combobox.Options>
          </div>
        )}
      </Combobox>

      {error && errormsj ? (
        <span className="flex flex-wrap items-center text-base font-medium text-primary">
          {errormsj}
        </span>
      ) : null}
    </div>
  );
}
